import { MenuItem, Stack, TextField } from '@mui/material';
import type { BookingTerms } from 'api-client/models';
import { apiDateToDateOnly } from '@/utils/dateUtils';

export const emptyBookingTerms: BookingTerms = {
  dealType: 'flat_rental', currency: 'USD', rentalFee: '', depositAmount: '0.00',
};

type Props = { value: BookingTerms; onChange: (value: BookingTerms) => void; disabled?: boolean };

export function BookingTermsFields({ value, onChange, disabled = false }: Props) {
  const field = (key: keyof BookingTerms, next: string): void => onChange({ ...value, [key]: next });
  return <Stack spacing={2}>
    <TextField select label="Deal type" value={value.dealType} disabled={disabled}
      onChange={event => onChange(event.target.value === 'flat_rental'
        ? { ...emptyBookingTerms, currency: value.currency }
        : { dealType: 'revenue_share', currency: value.currency, hostPercentage: '', settlementBasis: 'gross_ticket_sales', depositAmount: '0.00' })}>
      <MenuItem value="flat_rental">Flat venue rental</MenuItem>
      <MenuItem value="revenue_share">Revenue sharing</MenuItem>
    </TextField>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextField label="Currency" value={value.currency ?? 'USD'} disabled={disabled} fullWidth
        onChange={event => field('currency', event.target.value.toUpperCase())} helperText="Three-letter currency code, such as USD." />
      {value.dealType === 'flat_rental'
        ? <TextField label="Rental fee" type="number" required fullWidth disabled={disabled} value={value.rentalFee ?? ''}
          onChange={event => field('rentalFee', event.target.value)} inputProps={{ min: 0, step: '0.01' }} />
        : <TextField label="Host share (%)" type="number" required fullWidth disabled={disabled} value={value.hostPercentage ?? ''}
          onChange={event => field('hostPercentage', event.target.value)} inputProps={{ min: 0, max: 100, step: '0.01' }} />}
    </Stack>
    {value.dealType === 'revenue_share' && <TextField select label="Revenue-share basis" value={value.settlementBasis ?? 'gross_ticket_sales'}
      disabled={disabled} onChange={event => field('settlementBasis', event.target.value)}
      helperText="Only ticket sales and expenses agreed for this split are shared. Internal costs remain private.">
      <MenuItem value="gross_ticket_sales">Gross ticket sales</MenuItem>
      <MenuItem value="net_ticket_sales">Ticket sales minus agreed expenses</MenuItem>
    </TextField>}
    <TextField label="Deposit amount" type="number" value={value.depositAmount ?? '0.00'} disabled={disabled}
      onChange={event => field('depositAmount', event.target.value)} inputProps={{ min: 0, step: '0.01' }} />
    <TextField label="Deposit due date" type="date" value={value.depositDueDate ? apiDateToDateOnly(value.depositDueDate) : ''}
      disabled={disabled} InputLabelProps={{ shrink: true }} onChange={event => onChange({ ...value,
        depositDueDate: event.target.value ? new Date(`${event.target.value}T00:00:00Z`) : undefined,
      })} />
    <TextField label="Booking contact" value={value.bookingContact ?? ''} disabled={disabled}
      onChange={event => field('bookingContact', event.target.value)} helperText="Only include contact information intended for the other business." />
    <TextField label="Cancellation terms" multiline minRows={2} value={value.cancellationTerms ?? ''} disabled={disabled}
      onChange={event => field('cancellationTerms', event.target.value)} />
    <TextField label="Production requirements" multiline minRows={2} value={value.productionRequirements ?? ''} disabled={disabled}
      onChange={event => field('productionRequirements', event.target.value)} />
  </Stack>;
}
